import { VideoEventResponse, VideoEventWorkflowStatusIdEnum } from '$Generated/api';
import { React, moment } from 'Imports';
import { eventStatusIndicatorColors } from '$CSS/eventStatusIndicatorColors';
import { getStatusLabel } from '$Utilities/videoEventStatusUtility';
import { Card, Divider, Tooltip } from 'MaterialUIComponents';
import * as VideoAssets from '$Components/Shared/VideoAssetsUrls';
import { ConfigService } from '$State/ConfigFreezerService';
import { isEncompass } from '$Utilities/integrationPlatform';
import { calcRequestStatus } from '$Utilities/dataModelUtilities';
import { VideoEventCardThumbnail } from '$Components/VideoEvents/VideoEventCardThumbnail/VideoEventCardThumbnail';
import * as DateFormatter from '$Components/Shared/DateFormatter';
import { faBullhorn, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PromoteRecallDialog from '../PromoteRecallDialog';
import DeleteRecallDialog from '../DeleteRecallDialog';
import { useState } from 'react';
import { canEditByRole, RolesEnum } from '$Externals/VerifyRole';
import { TruncatedText } from '$Components/Shared/TruncatedText';

const styles = require('./VideoRecallCard.scss') as {
    geotab: string;
    encompass: string;
    cardContainer: string;
    indicator: string;
    mediaContainer: string;
    failedThumbnailPreview: string;
    dataContainer: string;
    data: string;
    dataLabel: string;
    dataValue: string;
    requestData: string;
    scrollableInfo: string;
    actionsContainer: string;
    divider: string;
    clickable: string;
    icon: string;
};

function formatDuration(startDate: Date | string | undefined, endDate: Date | string | undefined): string {
    if (!startDate || !endDate) return '';
    const dur = moment.duration(moment(endDate).diff(moment(startDate)));
    const minutes = Math.floor(dur.asMinutes());
    const seconds = dur.seconds();
    const parts: string[] = [];
    if (minutes > 0) parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
    if (seconds > 0) parts.push(`${seconds} second${seconds === 1 ? '' : 's'}`);
    return parts.join(' ');
}

interface VideoRecallCardProps {
    videoEvent: VideoEventResponse;
    onSelectVideoEvent: (eventId: string, goBack: boolean) => void;
    updateVideoEventWorkflowStatus: (eventId: number | undefined) => void;
    onPromotedVideo: (eventId: number) => void;
    onDeletedVideo: (eventId: number) => void;
}

const VideoRecallCard = (props: VideoRecallCardProps) => {
    const { videoEvent } = props;
    const [isPromoteDialogOpen, setIsPromoteDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const userCanDelete = canEditByRole([RolesEnum.videoAdmin]);
    const successRecall = videoEvent.workflowStep === 'Success';
    const eventId = videoEvent.id ?? 0;
    const displayEventId = videoEvent.displayId ?? '';
    const canNavigateToDetails = videoEvent.workflowStep === 'Success' && displayEventId !== '';
    const { ThumbnailUrl: thumbnailURL, PreviewUrl: previewUrl } = VideoAssets.findPreviewUrls(
        videoEvent.videoAsset || [],
        ConfigService.getBaseUrl(),
    );

    const indicatorColor =
        videoEvent.videoEventStatus !== undefined ? eventStatusIndicatorColors[videoEvent.videoEventStatus] : { background: '', color: '' };

    const indicatorText =
        videoEvent.videoEventStatus !== undefined ? getStatusLabel(videoEvent.videoEventStatus as VideoEventWorkflowStatusIdEnum) : '';

    const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (canNavigateToDetails) {
            props.onSelectVideoEvent(displayEventId, false);
        } else {
            e.stopPropagation();
        }
    };

    const handleOnPromoteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setIsPromoteDialogOpen(true);
    };

    const handleOnDeleteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setIsDeleteDialogOpen(true);
    };

    const durationText = formatDuration(videoEvent.eventStartDate, videoEvent.eventEndDate);
    const tooltipStyle = isEncompass
        ? {
              padding: '8px',
              borderRadius: '5px',
              backgroundColor: '#373E40',
              marginBottom: '5px !important',
              fontSize: '12px',
              fontWeight: '400',
          }
        : {
              padding: '10px',
              borderRadius: '8px',
              backgroundColor: '#3C5063',
              marginBottom: '5px !important',
              fontSize: '14px',
              fontWeight: '400',
          };

    return (
        <div className={isEncompass ? styles.encompass : styles.geotab}>
            <div className={styles.cardContainer}>
                <Card elevation={0}>
                    <div className={styles.mediaContainer}>
                        {!successRecall ? (
                            <div className={styles.failedThumbnailPreview}>{calcRequestStatus(videoEvent)}</div>
                        ) : (
                            <>
                                {indicatorColor.background && (
                                    <div
                                        className={styles.indicator}
                                        style={{ background: indicatorColor.background, color: indicatorColor.color }}
                                    >
                                        {indicatorText}
                                    </div>
                                )}

                                <VideoEventCardThumbnail
                                    eventId={eventId}
                                    displayEventId={displayEventId}
                                    thumbnailURL={thumbnailURL}
                                    videoURL={previewUrl}
                                    height={140}
                                    onSelectVideoEvent={props.onSelectVideoEvent}
                                    handleVideoPlayed={props.updateVideoEventWorkflowStatus}
                                />
                            </>
                        )}
                    </div>
                    <div className={`${styles.dataContainer} ${canNavigateToDetails ? styles.clickable : ''}`} onClick={handleCardClick}>
                        <div className={styles.scrollableInfo}>
                            <div className={styles.data}>
                                <div className={styles.dataLabel}>Vehicle</div>
                                <TruncatedText className={styles.dataValue}>
                                    {videoEvent.vehicleDescription?.trim() || videoEvent.integrationPartnerVehicleId || 'Unknown'}
                                </TruncatedText>
                            </div>
                            <div className={styles.data}>
                                <div className={styles.dataLabel}>Date/Time</div>
                                <TruncatedText className={styles.dataValue}>
                                    {DateFormatter.dateAndTimezoneWithSeconds(moment(videoEvent.eventStartDate!))}
                                </TruncatedText>
                            </div>
                            <div className={styles.data}>
                                <div className={styles.dataLabel}>Duration</div>
                                <TruncatedText className={styles.dataValue}>{durationText}</TruncatedText>
                            </div>
                            <div className={styles.requestData}>
                                {`Requested on ${DateFormatter.tzDate(moment(videoEvent.requestedOn!))} at ${DateFormatter.timeWithTimezone(moment(videoEvent.requestedOn!))} by ${videoEvent.requestedBy ?? 'Unknown'}`}
                            </div>
                        </div>
                        <Divider flexItem className={styles.divider} />
                        <div className={styles.actionsContainer}>
                            <Tooltip
                                title={successRecall ? "Promote" : ""}
                                placement={'top'}
                                slotProps={{ tooltip: { sx: tooltipStyle }, arrow: { sx: { color: '#3C5063' } } }}
                                arrow={!isEncompass}
                            >
                                <span>
                                    <button className={styles.icon} onClick={handleOnPromoteClick} disabled={!successRecall}>
                                        <FontAwesomeIcon icon={faBullhorn} />
                                    </button>
                                </span>
                            </Tooltip>
                            <Tooltip
                                title={!userCanDelete ? "" : "Delete"}
                                placement="top"
                                slotProps={{ tooltip: { sx: tooltipStyle }, arrow: { sx: { color: '#3C5063' } } }}
                                arrow={!isEncompass}
                            >
                                <span>
                                    <button
                                        className={styles.icon}
                                        onClick={handleOnDeleteClick}
                                        disabled={!userCanDelete}
                                    >
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                    </button>
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                </Card>
            </div>
            <PromoteRecallDialog
                videoEventId={eventId}
                open={isPromoteDialogOpen}
                onPromote={() => {
                    setIsPromoteDialogOpen(false);
                    props.onPromotedVideo(eventId);
                }}
                onClose={() => setIsPromoteDialogOpen(false)}
            />
            <DeleteRecallDialog
                videoEventId={eventId}
                open={isDeleteDialogOpen}
                onDelete={() => {
                    setIsDeleteDialogOpen(false);
                    props.onDeletedVideo(eventId);
                }}
                onClose={() => setIsDeleteDialogOpen(false)}
            />
        </div>
    );
};

export default VideoRecallCard;
